import { BASE_URL } from "@/constants/apiConfig";
import { GDTrackerInitPromise } from "@/tracker";
import gdABTest from "@gaoding/gd-abtest";
import { isCancel } from "axios";
import { encode } from "js-base64";
import { cloneDeep } from "lodash";
import { match } from "path-to-regexp";
import { getAccessToken } from "./auth";
import { Cache } from "./cache";
import createRequest from "./createRequest";
import { gdSlsLog } from "./gdSlsRum";

export const env = process.env.NEXT_PUBLIC_DEPLOY_ENV || "prod";

export const REQUEST_MESSAGE_KEY = "REQUEST_MESSAGE_KEY";

// 只在服务端使用的内部请求
const InnerHuabanApi = {
  dev: "http://api-dev.s.huaban.com",
  fat: "http://api-fat.s.huaban.com",
  stage: "http://api-stage.s.huaban.com",
  prod: "http://api.s.huaban.com",
};
const InnerBaseURL = InnerHuabanApi[env];

/**
 * @type {Cache}
 */
let requestCache;

/**
 * 检查URL是否匹配实验路径
 * @param {string} url 待匹配的URL
 * @param {string} expPath 实验路径模式
 * @returns {boolean} 是否匹配
 */
function isUrlMatchExpPath(url, expPath) {
  try {
    // 处理*号通配符场景
    if (expPath.endsWith("*")) {
      const prefix = expPath.slice(0, -1); // 移除末尾的*
      return url.startsWith(prefix);
    }

    // 使用path-to-regexp进行精确匹配
    return match(expPath)(url);
  } catch (error) {
    // 匹配AB实验接口出错，不影响其他匹配逻辑
    console.error("接口匹配AB实验接口出错", error);
    gdSlsLog.error("接口匹配AB实验接口出错", error);
    return false;
  }
}

/**
 * 获取需要添加的abtest实验标识头信息
 * @param {*} url
 * @returns
 */
async function getABTestHeader(url) {
  return new Promise(async (resolve) => {
    try {
      // 1秒超时，则继续执行，不阻塞请求
      setTimeout(() => {
        resolve({});
      }, 1000);

      // 等待abtest初始化完成
      if (GDTrackerInitPromise) {
        await GDTrackerInitPromise;
      }

      if (!gdABTest.option || typeof window === "undefined") {
        // gdABTest未初始化；
        return resolve({});
      }

      const exps = await gdABTest.getExps();

      if (!exps) return {};

      const xAbtestTags = {};
      var hasMatch = false;
      exps.forEach((exp) => {
        if (
          exp.experimentUrls.some((expPath) => isUrlMatchExpPath(url, expPath))
        ) {
          xAbtestTags[exp.variableName] = exp.variableValue;
          hasMatch = true;
        }
      });

      // 有匹配的实验需要添加标识
      if (hasMatch) {
        const { trackerWind } = window.gdTracker;

        const header = {
          "guest-token": trackerWind.get("common", "device_id").device_id,
          "x-guest-token": trackerWind.get("common", "device_id").device_id,
          "x-abtest-tags": encode(JSON.stringify(xAbtestTags)),
        };

        resolve(header);
      } else {
        resolve({});
      }
    } catch (error) {
      console.error("请求获取abtest标识出错", error);
      resolve({});
    }
  });
}

function isEncoded(str) {
  try {
    return decodeURIComponent(str) !== str;
  } catch (e) {
    // 如果解码失败，说明字符串不是有效的编码
    return false;
  }
}

function encodeUrl(url) {
  const [basePath, queryString] = url.split("?"); // 分割路径和查询参数部分
  // 对路径部分进行转义
  const encodedPath = basePath
    .split("/")
    .map((str) => {
      return isEncoded(str) ? str : encodeURIComponent(str);
    }) // 逐段转义
    .join("/");

  return queryString ? `${encodedPath}?${queryString}` : encodedPath;
}

const baseAxios = createRequest({
  BaseURL: BASE_URL,
});

const innerAxios = createRequest({
  BaseURL: InnerBaseURL,
});

/**
 * 公共请求方法
 * @param {{
 *  url: string,
 *  params?: Object,
 *  query?: Object,
 *  ignoreMessage?: boolean,
 *  ignoreError?: boolean,
 *  ignoreGdPermission?: boolean,
 *  ignoreVerify?: boolean,
 *  returnRes?: boolean,
 *  serverSideHttps?: boolean,
 *  cache?: boolean | {time: number}
 *  inner?: boolean
 * } & import('axios').AxiosRequestConfig} param
 * @returns
 */
const request = async ({
  url = "/",
  params, // url匹配参数
  query, // 查询参数
  headers = {},
  ignoreMessage,
  ignoreGdPermission,
  ignoreError,
  ignoreCancel,
  ignoreVerify, //忽略滑块验证
  returnRes, // 返回响应对象
  cache,
  serverSideHttps,
  inner,
  ...opts
}) => {
  try {
    const abtestHeader = await getABTestHeader(url);

    const accessToken = await getAccessToken(headers?.cookie);

    let cacheKey;
    if (cache) {
      if (!requestCache) {
        requestCache = new Cache();
      }

      cacheKey = requestCache.generateKey({
        url,
        params,
        query,
        headers,
        ...opts,
      });

      const cacheData = requestCache.get(cacheKey);

      if (cacheData) {
        return cacheData;
      }
    }

    const axios = inner ? innerAxios : baseAxios;

    if (opts.retryTimes > 3) {
      return Promise.reject({
        code: "REQUEST_RETRY_TIMES_EXCEEDED",
        message: "请求重试次数超过3次",
      });
    }

    const res = await axios({
      url: encodeUrl(url),
      params: query,
      options: {
        params,
        ignoreMessage,
        ignoreError,
        ignoreCancel,
        ignoreGdPermission,
        ignoreVerify,
        returnRes,
        accessToken,
        request,
      },
      timeout: 10000,
      headers: {
        ...headers,
        ...abtestHeader,
        ...(accessToken
          ? {
              authorization: `Bearer ${accessToken}`,
            }
          : null),
      },
      ...opts,
    });

    if (cache) {
      requestCache.set(cacheKey, cloneDeep(res), {
        expireTime: cache?.time || 60000,
      });
    }

    return res;
  } catch (error) {
    const { response } = error;
    if (
      !ignoreError &&
      typeof window !== "undefined" &&
      (!isCancel(error) || !ignoreCancel)
    ) {
      // 兼容老的错误处理逻辑
      throw error;
    }

    if (response?.data) {
      return response.data;
    }

    return error;
  }
};

export default request;
